import {
  ArrowLeft,
  Button,
  CreditCard,
  Gift,
  Mail,
  Phone,
  Stethoscope,
  User,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "../components/atoms/LoadingSpinner/LoadingSpinner";
import { StatusBadge } from "../components/molecules/StatusBadge/StatusBadge";
import { useSpecialtyLabels } from "../hooks/useSpecialtyLabels";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectSession } from "../store/selectors/sessionSelector";
import {
  clearError,
  clearSelectedDoctor,
  clearSmsStatus,
  fetchAuditLogRequest,
  fetchDoctorDetailRequest,
  fetchGrantHistoryRequest,
  fetchSmsStatusRequest,
  grantFreeAccessRequest,
  selectAuditLog,
  selectAuditLogLoading,
  selectDoctorDetailError,
  selectDoctorDetailLoading,
  selectGrantHistory,
  selectGrantHistoryLoading,
  selectGrantSaving,
  selectSelectedDoctor,
  selectSmsStatus,
  selectSmsStatusError,
  selectSmsStatusLoading,
} from "../store/reducers/doctorAdminSlice";
import { AuditLogSection } from "./components/AuditLogSection";
import { GrantFreeAccessModal } from "./components/GrantFreeAccessModal";
import { GrantHistorySection } from "./components/GrantHistorySection";
import { SmsStatusPanel } from "./components/SmsStatusPanel";
import { GrantFreeAccessPayload } from "../types/doctorAdmin.definition";
import { RoleEnum } from "../types/user.definition";
import { formatDateTime } from "../utils/date-format-utils";
import { licenceValueLabel } from "../utils/enum-label-utils";

// MED-1253: the specific BFF/API error code returned when trying to grant
// free access to a doctor with an active PAID licence — surfaced inline in
// the modal instead of a generic toast.
const FREE_ACCESS_BLOCKED_ERROR_CODE =
  "FREE_ACCESS_BLOCKED_ACTIVE_PAID_LICENCE";

export const DoctorDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { doctorId } = useParams<{ doctorId: string }>();
  // MED-1523: the doctor payload embeds untranslated specialty labels.
  const specialtyLabel = useSpecialtyLabels();

  const doctor = useAppSelector(selectSelectedDoctor);
  const loading = useAppSelector(selectDoctorDetailLoading);
  const error = useAppSelector(selectDoctorDetailError);
  const smsStatus = useAppSelector(selectSmsStatus);
  const smsStatusLoading = useAppSelector(selectSmsStatusLoading);
  const smsStatusError = useAppSelector(selectSmsStatusError);
  const grantSaving = useAppSelector(selectGrantSaving);
  const grantHistory = useAppSelector(selectGrantHistory);
  const grantHistoryLoading = useAppSelector(selectGrantHistoryLoading);
  const auditLog = useAppSelector(selectAuditLog);
  const auditLogLoading = useAppSelector(selectAuditLogLoading);
  const session = useAppSelector(selectSession);
  // MED-1256: "Grant free access" is a sensitive action reserved for
  // SUPER_ADMIN — ADMIN can view everything else on this page, but must not
  // see the trigger for it.
  const isSuperAdmin = session?.user?.roleName === RoleEnum.SUPER_ADMIN;

  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [grantInlineError, setGrantInlineError] = useState<string | null>(null);

  // Tracks the currently-viewed doctorId so a grant request's onSuccess
  // callback (closure-captured at submit time) can detect that the user has
  // since navigated to a different doctor's page before refreshing history.
  const currentDoctorIdRef = useRef(doctorId);
  useEffect(() => {
    currentDoctorIdRef.current = doctorId;
  }, [doctorId]);

  useEffect(() => {
    if (doctorId) {
      dispatch(fetchDoctorDetailRequest(doctorId));
      dispatch(fetchSmsStatusRequest(doctorId));
      dispatch(fetchGrantHistoryRequest(doctorId));
      dispatch(fetchAuditLogRequest(doctorId));
    }
  }, [doctorId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSelectedDoctor());
      dispatch(clearSmsStatus());
    };
  }, [dispatch]);

  const fullName = doctor
    ? [doctor.firstName, doctor.lastName].filter(Boolean).join(" ") || "—"
    : "—";

  const openGrantModal = () => {
    setGrantInlineError(null);
    setIsGrantModalOpen(true);
  };

  const closeGrantModal = () => {
    setIsGrantModalOpen(false);
    setGrantInlineError(null);
  };

  const handleGrantSubmit = (data: GrantFreeAccessPayload) => {
    if (!doctorId) return;
    setGrantInlineError(null);
    dispatch(
      grantFreeAccessRequest({
        doctorId,
        data,
        onSuccess: () => {
          closeGrantModal();
          toast.success(t("admin.doctors.grant.toast.success"));
          // If the user has since navigated to a different doctor's page,
          // don't overwrite that doctor's history/audit log with this one's.
          if (currentDoctorIdRef.current !== doctorId) return;
          dispatch(fetchGrantHistoryRequest(doctorId));
          // The grant itself is a sensitive action recorded in the audit log
          // (MED-1255) — refresh it too so it doesn't show stale data.
          dispatch(fetchAuditLogRequest(doctorId));
        },
        onError: code => {
          if (code === FREE_ACCESS_BLOCKED_ERROR_CODE) {
            setGrantInlineError(t("admin.doctors.grant.blockedPaidLicence"));
          } else {
            toast.error(t("admin.doctors.grant.toast.error"));
          }
        },
      })
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/doctors")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>{t("common.back")}</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("admin.doctors.detail.error")}
            </h1>
          </div>
        </div>
      </div>
    );
  }

  // Doctor not found
  if (!doctor) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/doctors")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>{t("common.back")}</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("admin.doctors.detail.notFound")}
            </h1>
            <p className="text-gray-600">
              {t("admin.doctors.detail.notFoundDescription")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center page-header">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/doctors")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>{t("common.back")}</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{fullName}</h1>
            <p className="text-gray-600">
              {t("admin.doctors.detail.subtitle")}
            </p>
          </div>
        </div>
      </div>

      {/* Identity */}
      <div className="page-section card-container">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("admin.doctors.detail.identity.title")}
          </h3>
          <div className="flex items-center gap-2">
            <StatusBadge
              status={
                doctor.disabled
                  ? t("admin.doctors.detail.status.disabled")
                  : t("admin.doctors.detail.status.active")
              }
              variant={doctor.disabled ? "error" : "success"}
            />
            <StatusBadge
              status={
                doctor.accountVerified
                  ? t("admin.doctors.detail.status.verified")
                  : t("admin.doctors.detail.status.unverified")
              }
              variant={doctor.accountVerified ? "primary" : "neutral"}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex items-center text-gray-600">
            <User className="flex-shrink-0 mr-3 w-5 h-5" />
            <span>{fullName}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Phone className="flex-shrink-0 mr-3 w-5 h-5" />
            <span>
              {doctor.phoneNumber
                ? formatPhoneNumberForDisplay(doctor.phoneNumber)
                : "—"}
            </span>
          </div>
          <div className="flex items-center text-gray-600">
            <Mail className="flex-shrink-0 mr-3 w-5 h-5" />
            <span>{doctor.email ?? "—"}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Stethoscope className="flex-shrink-0 mr-3 w-5 h-5" />
            <span>
              {doctor.specialties && doctor.specialties.length > 0
                ? doctor.specialties
                    .map(s => specialtyLabel(s))
                    .filter(Boolean)
                    .join(", ") || "—"
                : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Licence */}
      <div className="page-section card-container">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("admin.doctors.detail.licence.title")}
          </h3>
          <div className="flex items-center gap-3">
            {isSuperAdmin && (
              <Button
                variant="outline"
                onClick={openGrantModal}
                className="flex items-center space-x-2"
              >
                <Gift className="w-4 h-4" />
                <span>{t("admin.doctors.grant.button")}</span>
              </Button>
            )}
            <CreditCard className="w-5 h-5 text-gray-400" />
          </div>
        </div>
        {doctor.licence ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-xs text-neutral-500">
                {t("admin.doctors.detail.licence.plan")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {licenceValueLabel(t, doctor.licence.planType)}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">
                {t("admin.doctors.detail.licence.status")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {t(`doctorLicence.section.statuses.${doctor.licence.status}`, {
                  defaultValue: doctor.licence.status,
                })}
              </p>
            </div>
            {/* MED-1524: the card showed only the plan and the status, so the
                administrator could not see when the licence runs out without
                opening the extend-access dialog or the Licences page. Laid out
                like the facility detail page's licence card, and formatted
                through the same shared formatter — a Java LocalDate arrives as
                "2026-10-30" or [Y, M, D]. */}
            <div>
              <p className="text-xs text-neutral-500">
                {t("admin.doctors.detail.licence.startDate")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {formatDateTime(doctor.licence.startDate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">
                {t("admin.doctors.detail.licence.endDate")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {formatDateTime(doctor.licence.endDate)}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm font-medium text-neutral-700">
              {t("admin.doctors.detail.licence.noLicence")}
            </p>
          </div>
        )}
      </div>

      {/* SMS Notifications */}
      <SmsStatusPanel
        status={smsStatus}
        loading={smsStatusLoading}
        error={smsStatusError}
      />

      {/* Grant / Extension History */}
      <GrantHistorySection
        history={grantHistory}
        loading={grantHistoryLoading}
      />

      {/* Activity / Audit Log — general audit trail of sensitive admin
          actions taken on this doctor's account (MED-1255). Distinct from
          the Grant / Extension History section above. */}
      <AuditLogSection auditLog={auditLog} loading={auditLogLoading} />

      <GrantFreeAccessModal
        isOpen={isGrantModalOpen}
        onClose={closeGrantModal}
        onSubmit={handleGrantSubmit}
        currentLicence={doctor.licence}
        isSaving={grantSaving}
        inlineError={grantInlineError}
      />
    </div>
  );
};
